import React, { useEffect, useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import RegisterPage from "./pages/RegisterPage";
import LoginPage from "./pages/LoginPage";

import HomePage from "./pages/HomePage";
import ProfilePage from "./pages/ProfilePage";
import ProfileEditPage from "./pages/ProfileEditPage";
import NuevaOfertaPage from "./pages/NuevaOfertaPage";
import OfferDetailPage from "./pages/OfferDetailPage";
import OffersListPage from "./pages/OffersListPage";
import OfferResponsePage from "./pages/OfferResponsePage";
import CounterOfferPage from "./pages/CounterOfferPage";
import ReformulationResponsePage from "./pages/ReformulationResponsePage";
import PurchasePage from "./pages/PurchasePage";
import PurchaseBillingPage from "./pages/PurchaseBillingPage";
import PurchaseSuccessPage from "./pages/PurchaseSuccessPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CreditsProvider } from "./context/CreditsContext";
import { FormProvider } from "./context/FormContext";
import { ColumnsProvider } from "./context/ColumnsContext";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import GlobalStyles from "./utils/globalStyles";

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const AppContent = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Protected route component
  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return children;
  };

  // Public route component
  const PublicRoute = ({ children }) => {
    if (isAuthenticated) {
      return <Navigate to="/offers" replace />;
    }
    return children;
  };

  // Determine if current route is protected
  const isProtectedRoute =
    location.pathname === "/home" || location.pathname === "/profile";

  return (
    <Routes>
      <Route path="/" element={
        <ProtectedRoute>
          <OffersListPage />
        </ProtectedRoute>
      } />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/home"
        element={
          <HomePage />
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/edit"
        element={
          <ProtectedRoute>
            <ProfileEditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/offers"
        element={
          <ProtectedRoute>
            <OffersListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nueva-oferta"
        element={
          <ProtectedRoute>
            <NuevaOfertaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/editar-oferta/:id"
        element={
          <ProtectedRoute>
            <NuevaOfertaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/detalle-oferta/:id"
        element={
          <ProtectedRoute>
            <OfferDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/offer/:id"
        element={
          <ProtectedRoute>
            <OfferDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/offer/:id/response"
        element={
          <OfferResponsePage />
        }
      />
      <Route
        path="/offers/respond"
        element={<OfferResponsePage />}
      />
      <Route
        path="/counter-offer/:id"
        element={
          <CounterOfferPage />
        }
      />
      <Route
        path="/offers/counteroffer"
        element={<CounterOfferPage />}
      />
      <Route
        path="/reformulation-response/:id"
        element={
          <ReformulationResponsePage />
        }
      />
      <Route
        path="/offers/reformulation"
        element={<ReformulationResponsePage />}
      />
      <Route
        path="/purchase"
        element={
          <ProtectedRoute>
            <PurchasePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase/billing"
        element={
          <ProtectedRoute>
            <PurchaseBillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase/success"
        element={
          <ProtectedRoute>
            <PurchaseSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/privacy"
        element={<PrivacyPage />}
      />
      <Route
        path="/privacidad"
        element={
          <PublicRoute>
            <PrivacyPage />
          </PublicRoute>
        }
      />
      <Route
        path="/terms"
        element={<TermsPage />}
      />
      <Route
        path="/terminos"
        element={
          <PublicRoute>
            <TermsPage />
          </PublicRoute>
        }
      />
      <Route path="*" element={<Navigate replace to="/offers" />} />
    </Routes>
  );
};

// Push GTM page_view events on SPA route changes with StrictMode-safe deduplication
const AnalyticsListener = () => {
  const location = useLocation();
  const lastPathRef = useRef("");

  useEffect(() => {
    // Ensure dataLayer exists
    window.dataLayer = window.dataLayer || [];

    const page_path = `${location.pathname}${location.search}`;
    const page_title = document.title || "";
    const page_location = window.location.href;

    // Deduplicate across StrictMode remounts and rapid re-renders
    if (window.__lastPagePathPushed === page_path || lastPathRef.current === page_path) {
      return;
    }
    window.__lastPagePathPushed = page_path;
    lastPathRef.current = page_path;

    window.dataLayer.push({
      event: "page_view",
      page_path,
      page_title,
      page_location,
    });
  }, [location]);

  return null;
};

const App = () => {
  return (
    <AppContainer>
      <GlobalStyles />
      <AuthProvider>
        <CreditsProvider>
          <FormProvider>
            <ColumnsProvider>
              <AnalyticsListener />
              <AppContent />
            </ColumnsProvider>
          </FormProvider>
        </CreditsProvider>
      </AuthProvider>
    </AppContainer>
  );
};

export default App;
